import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddToHomescreenIcon from '@mui/icons-material/AddToHomeScreen';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useInstallPrompt from '../../hooks/useInstallPrompt';
import { DEFAULT_THEME_COLOR, DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER } from '../../constants/theme';

const Step = ({ number, icon, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25 }}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: `${DEFAULT_THEME_COLOR}22`,
        border: `1px solid ${DEFAULT_THEME_COLOR}55`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography sx={{ color: DEFAULT_THEME_COLOR, fontSize: '0.75rem', fontWeight: 700 }}>
        {number}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.88rem' }}>{text}</Typography>
    </Box>
  </Box>
);

const AddToHomeScreenButton = ({ variant = 'contained', size = 'large', label, sx = {} }) => {
  const { deferredPrompt, isIos, isInStandaloneMode, isInstalled, triggerInstall } = useInstallPrompt();
  const [iosDialogOpen, setIosDialogOpen] = useState(false);

  if (isInstalled || isInStandaloneMode) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
        <CheckCircleIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 20 }} />
        <Typography sx={{ color: DEFAULT_THEME_COLOR, fontSize: '0.875rem', fontWeight: 600 }}>
          App installed
        </Typography>
      </Box>
    );
  }

  const buttonLabel = label || 'Add to Home Screen';

  const handleClick = () => {
    if (isIos) {
      setIosDialogOpen(true);
    } else if (deferredPrompt) {
      triggerInstall();
    } else {
      // Fallback: browser menu hint
      setIosDialogOpen(true);
    }
  };

  const showButton = isIos || !!deferredPrompt;
  if (!showButton) return null;

  return (
    <>
      <Button
        size={size}
        variant={variant}
        startIcon={<AddToHomescreenIcon />}
        onClick={handleClick}
        sx={
          variant === 'contained'
            ? {
                background: DEFAULT_GRADIENT,
                color: 'white',
                fontWeight: 700,
                '&:hover': { background: DEFAULT_GRADIENT_HOVER },
                ...sx,
              }
            : {
                borderColor: DEFAULT_THEME_COLOR,
                color: DEFAULT_THEME_COLOR,
                fontWeight: 600,
                '&:hover': { backgroundColor: `${DEFAULT_THEME_COLOR}15`, borderColor: DEFAULT_THEME_COLOR },
                ...sx,
              }
        }
      >
        {buttonLabel}
      </Button>

      {/* iOS instructions dialog */}
      <Dialog
        open={iosDialogOpen}
        onClose={() => setIosDialogOpen(false)}
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #0d1b2a 0%, #091420 100%)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 3,
            maxWidth: 360,
            width: '100%',
            m: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pt: 2.5, pb: 0 }}>
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
            Add to Home Screen
          </Typography>
          <IconButton size="small" onClick={() => setIosDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.5)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 1.5, pb: 2.5 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', mb: 2 }}>
            Follow these steps in Safari to install Freakland on your home screen:
          </Typography>

          <Step
            number="1"
            icon={<IosShareIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 20, flexShrink: 0 }} />}
            text='Tap the Share button at the bottom of your browser'
          />
          <Step
            number="2"
            icon={<AddBoxOutlinedIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 20, flexShrink: 0 }} />}
            text='"Add to Home Screen" from the menu'
          />
          <Step
            number="3"
            icon={<CheckCircleIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 20, flexShrink: 0 }} />}
            text='Tap "Add" — Freakland will appear as an app icon'
          />

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              background: `${DEFAULT_THEME_COLOR}12`,
              border: `1px solid ${DEFAULT_THEME_COLOR}30`,
            }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' }}>
              Works best in <strong style={{ color: 'white' }}>Safari</strong> on iPhone and iPad.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddToHomeScreenButton;
